// Notice a deploy BEFORE it breaks a navigation.
//
// chunk-recovery.ts cures a stale tab after the fact: a click asks for a page
// file the deploy removed, the import fails, and a toast offers the reload. That
// works, but the page they clicked stays empty until they take it — and before
// every tab had that fix, a red "Render error" (reported 24 Sep 2026, on an
// environment that deploys several times an hour).
//
// This is the step before. Now and then — when the tab comes back into view,
// on navigation, and every few minutes while visible — ask the server which
// build it is serving. If it is not the one this tab is running, the next page
// that needs a file loads the new build as an ordinary page load instead
// (App.tsx, lazyPage), and nothing fails at all.
//
// The tab also shows the "new version available" toast as soon as the check
// sees the newer build, so a person who stays on one page still hears about it.
//
// The check is a revalidated GET of index.html (~1 kB, no-cache on the server)
// and is throttled, so an idle tab costs a request every few minutes and a
// hidden one costs nothing. Production only: the dev server has no hashed
// bundle to compare.

import { offerNewVersion } from "./chunk-recovery";

const CHECK_EVERY_MS = 5 * 60_000;
const MIN_GAP_MS = 60_000;
const BUNDLE_RE = /\/ops\/static\/assets\/index-[\w-]+\.js/;

let newerBuild = false;
let lastCheck = 0;
// A softphone call is ringing or live. A reload would hang it up, so while
// this is set the stale tab keeps going the old way (a page whose file still
// exists loads fine; one that is gone falls to chunk-recovery as before).
let busy = false;

/** The bundle this document booted from, e.g. /ops/static/assets/index-iqPg5crG.js. */
function runningBundle(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  if (!s) return null;
  return new URL(s.src, window.location.href).pathname.match(BUNDLE_RE)?.[0] ?? null;
}

/** Ask the server which build it serves. Throttled; failures are silent — a
 *  missed check only means we fall back to chunk-recovery. */
export async function checkForNewBuild(): Promise<void> {
  if (!import.meta.env.PROD || newerBuild) return;
  if (document.visibilityState !== "visible") return;
  const now = Date.now();
  if (now - lastCheck < MIN_GAP_MS) return;
  lastCheck = now;
  const mine = runningBundle();
  if (!mine) return;
  try {
    const r = await fetch("/ops/", { cache: "no-cache", credentials: "same-origin" });
    if (!r.ok) return;
    const live = (await r.text()).match(BUNDLE_RE)?.[0];
    if (live && live !== mine) {
      newerBuild = true;
      offerNewVersion();
    }
  } catch {
    /* offline / mid-deploy — try again next time */
  }
}

/** Start watching. Call once at boot. */
export function startBuildWatch(): void {
  if (!import.meta.env.PROD) return;
  document.addEventListener("visibilitychange", () => void checkForNewBuild());
  window.setInterval(() => void checkForNewBuild(), CHECK_EVERY_MS);
}

/** True when the server has moved on to a newer build and it is safe to
 *  reload this tab onto it. */
export function shouldLoadNewBuild(): boolean {
  return newerBuild && !busy;
}

export function setReloadBusy(value: boolean): void {
  busy = value;
}
